<template>
  <card>
    <div slot="header" class="commission-rules__header">
      <h4 class="card-title">Exception rules</h4>
      <base-button v-if="canManageAgencies" type="primary" size="sm" @click="openCreate">Add rule</base-button>
    </div>

    <div v-if="loading" class="commission-rules__muted">Loading...</div>
    <div v-else-if="loadError" class="commission-rules__error">{{ loadError }}</div>
    <div v-else-if="!rules.length" class="commission-rules__muted">
      No exception rules — the standard commission applies to every procedure.
    </div>

    <table v-else class="table table-responsive commission-rules__table">
      <thead>
        <tr>
          <th>ID</th>
          <th>Procedure</th>
          <th>Type</th>
          <th>Value</th>
          <th>Valid from</th>
          <th>Valid to</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="rule in rules" :key="rule.id">
          <td>{{ rule.id }}</td>
          <td>
            <span
              class="commission-rules__badge"
              :class="`commission-rules__badge--${rule.procedure && rule.procedure.type}`"
            >
              {{ procedureTypeLabel(rule.procedure && rule.procedure.type) }}
            </span>
            {{ procedureName(rule.procedure) }}
          </td>
          <td>{{ commissionTypeLabel(rule.commission_type) }}</td>
          <td class="commission-rules__nowrap">{{ formatValue(rule) }}</td>
          <td class="commission-rules__nowrap">{{ rule.valid_from }}</td>
          <td class="commission-rules__nowrap">
            <span v-if="rule.valid_to">{{ rule.valid_to }}</span>
            <span v-else class="commission-rules__muted">open</span>
          </td>
          <td v-if="canManageAgencies" class="commission-rules__actions">
            <button type="button" class="btn btn-sm btn-primary" title="Edit" @click="openEdit(rule)">
              <i class="fa fa-pencil-alt"></i>
            </button>
            <button
              type="button"
              class="btn btn-sm btn-danger"
              title="Delete"
              :disabled="removing === rule.id"
              @click="remove(rule)"
            >
              <i class="fa fa-trash"></i>
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <agency-commission-rule-dialog v-if="canManageAgencies"
      :visible.sync="dialogVisible"
      :agency-id="agencyId"
      :rule="editingRule"
      :currency-code="currencyCode"
      @saved="onSaved"
    />
  </card>
</template>

<script>
import agencyAccess from '@/components/Agencies/agencyAccess';
import APIAgencies from '@/api/modules/agencies';
import AgencyCommissionRuleDialog from './AgencyCommissionRuleDialog.vue';
import { procedureTypeLabel, commissionTypeLabel } from './commission';

export default {
  mixins: [agencyAccess],
  components: { AgencyCommissionRuleDialog },
  props: {
    agencyId: { type: Number, required: true },
    // Contract currency code, used to label fixed amounts.
    currencyCode: { type: String, default: '' },
  },
  data: () => ({
    rules: [],
    loading: false,
    loadError: '',
    removing: null,
    dialogVisible: false,
    editingRule: null,
  }),
  created() {
    this.load();
  },
  methods: {
    procedureTypeLabel,
    commissionTypeLabel,
    procedureName(procedure) {
      if (!procedure) return '—';
      return procedure.name || `#${procedure.id}`;
    },
    formatValue(rule) {
      if (rule.commission_type === 'zero') return '—';
      if (rule.commission_type === 'percentage') return `${rule.commission_value}%`;
      return this.currencyCode ? `${rule.commission_value} ${this.currencyCode}` : rule.commission_value;
    },
    async load() {
      this.loading = true;
      this.loadError = '';
      try {
        this.rules = await APIAgencies.getCommissionRules(this.agencyId);
        this.fillMissingNames();
      } catch (e) {
        this.loadError = `Failed to load exception rules${e && e.code ? ` (${e.code})` : ''}`;
      } finally {
        this.loading = false;
      }
    },
    // The rules list may return procedure.name = null; look those up by ids[] per type.
    // Best effort — on failure the table keeps showing "#id".
    async fillMissingNames() {
      const missing = {};
      this.rules.forEach((rule) => {
        const p = rule.procedure;
        if (p && !p.name && p.id) (missing[p.type] = missing[p.type] || new Set()).add(p.id);
      });
      await Promise.all(Object.keys(missing).map(async (type) => {
        try {
          const found = await APIAgencies.getCommissionProceduresByIds({ type, ids: [...missing[type]] });
          const names = found.reduce((acc, p) => ({ ...acc, [p.id]: p.name }), {});
          this.rules = this.rules.map((rule) => {
            const p = rule.procedure;
            if (!p || p.name || p.type !== type || !names[p.id]) return rule;
            return { ...rule, procedure: { ...p, name: names[p.id] } };
          });
        } catch (e) {
          // ignore
        }
      }));
    },
    openCreate() {
      if (!this.canManageAgencies) return;
      this.editingRule = null;
      this.dialogVisible = true;
    },
    openEdit(rule) {
      if (!this.canManageAgencies) return;
      this.editingRule = rule;
      this.dialogVisible = true;
    },
    onSaved() {
      this.$notify({ type: 'success', title: 'Rule saved' });
      // Re-fetch rather than splice: the server fills in the procedure name and ordering.
      this.load();
    },
    async remove(rule) {
      if (!this.canManageAgencies) return;
      try {
        await this.$confirm(
          `Delete rule #${rule.id} (${this.procedureName(rule.procedure)})?`,
          'Delete rule',
          { type: 'warning', confirmButtonText: 'Delete', cancelButtonText: 'Cancel' }
        );
      } catch (e) {
        return;
      }

      this.removing = rule.id;
      try {
        await APIAgencies.removeCommissionRule(this.agencyId, rule.id);
        this.rules = this.rules.filter((r) => r.id !== rule.id);
        this.$notify({ type: 'success', title: 'Rule deleted' });
      } catch (e) {
        if (e && e.is && e.is('not_found')) {
          this.rules = this.rules.filter((r) => r.id !== rule.id);
          this.$notify({ type: 'warning', title: 'Rule was already deleted' });
        } else {
          this.$notify({ type: 'error', title: `Failed to delete rule${e && e.code ? ` (${e.code})` : ''}` });
        }
      } finally {
        this.removing = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.commission-rules {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__muted {
    color: #8898aa;
    font-size: 13px;
  }

  &__error {
    color: #e3342f;
    font-size: 13px;
  }

  &__nowrap {
    white-space: nowrap;
  }

  &__actions {
    width: 1%;
    white-space: nowrap;
    text-align: right;

    .btn + .btn {
      margin-left: 4px;
    }
  }

  &__badge {
    display: inline-block;
    padding: 1px 8px;
    margin-right: 6px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    background: #e9ecef;
    color: #525f7f;

    &--operation {
      background: #e3f2fd;
      color: #1565c0;
    }

    &--diagnostic {
      background: #f3e5f5;
      color: #7b1fa2;
    }
  }
}
</style>
